import React, { useState, useMemo } from "react";
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Cell } from "recharts";
import { CheckCircle, AlertTriangle, ChevronDown, ChevronUp } from "lucide-react";
import { Card, SH, fmtM, SetupBanner, dealUrl, resolveF } from "../shared/constants";

const pct  = (v, d=0) => v==null?"—":`${(v*100).toFixed(d)}%`;
const clrS = (v, warn=60, good=80) => v>=good?"#16A34A":v>=warn?"#D97706":"#DC2626";
const bgS  = (v, warn=60, good=80) => v>=good?"rgba(22,163,74,0.08)":v>=warn?"rgba(217,119,6,0.08)":"rgba(220,38,38,0.07)";

// ── Tab button ────────────────────────────────────────────────────────────────
const Tab = ({ id, label, active, onClick, badge }) => (
  <button onClick={() => onClick(id)}
    style={{ padding:"8px 18px", borderRadius:8, border:"none", cursor:"pointer",
      background:active?"#CC1A1A":"transparent",
      color:active?"#fff":"#555", fontSize:12, fontWeight:active?700:500,
      display:"flex", alignItems:"center", gap:6 }}>
    {label}
    {badge!=null && <span style={{ fontSize:9, background:active?"rgba(255,255,255,0.25)":"rgba(0,0,0,0.08)",
      color:active?"#fff":"#555", borderRadius:10, padding:"1px 6px", fontWeight:700 }}>{badge}</span>}
  </button>
);

// ── Score bar ─────────────────────────────────────────────────────────────────
const ScoreBar = ({ val, max=5, color }) => (
  <div style={{ display:"flex", alignItems:"center", gap:8 }}>
    <div style={{ flex:1, height:5, background:"rgba(0,0,0,0.08)", borderRadius:3, overflow:"hidden" }}>
      <div style={{ width:`${(val/max)*100}%`, height:"100%", background:color, borderRadius:3 }}/>
    </div>
    <span style={{ fontSize:12, fontWeight:800, color, fontFamily:"'DM Mono',monospace", minWidth:28, textAlign:"right" }}>{val!=null?`${val}/${max}`:"—"}</span>
  </div>
);

// ── Stat tile ─────────────────────────────────────────────────────────────────
const Tile = ({ label, value, sub, color="#111" }) => (
  <div style={{ background:"#F8F8F8", borderRadius:9, padding:"12px 14px" }}>
    <div style={{ fontSize:10, color:"#555", fontWeight:600, textTransform:"uppercase", letterSpacing:"0.07em", marginBottom:6 }}>{label}</div>
    <div style={{ fontSize:22, fontWeight:700, color, fontFamily:"'DM Mono',monospace", marginBottom:3 }}>{value}</div>
    {sub && <div style={{ fontSize:10, color:"#555" }}>{sub}</div>}
  </div>
);

// ═══════════════════════════════════════════════════════════════════════════════
// TAB 1 — Overview (existing scores)
// ═══════════════════════════════════════════════════════════════════════════════
const OverviewTab = ({ metrics, settings }) => {
  const score  = metrics?.crmScore ?? null;
  const areas  = metrics?.crmAreas ?? null;
  const scoreColor = score==null?"#555":score>=7?"#16A34A":score>=5?"#C2610C":"#DC2626";
  const scoreLabel = score==null?"Awaiting Data":score>=8?"Highly Reliable":score>=6?"Mostly Reliable":score>=4?"Partially Reliable":"Unreliable";
  const completePct = metrics ? Math.round((metrics.completePct||0)*100) : null;

  const areaConfig = [
    {key:"pipelineStages",     label:"Stage Distribution"},
    {key:"fieldUsage",         label:"Field Usage"},
    {key:"dataCompleteness",   label:"Data Completeness"},
    {key:"forecastReliability",label:"Forecast Reliability"},
    {key:"activityTracking",   label:"Activity Logging"},
    {key:"pipelineQuality",    label:"Pipeline Quality"},
    {key:"dataIntegrity",      label:"Data Integrity"},
    {key:"dealCompleteness",   label:"Deal Completeness"},
    {key:"stageVelocity",      label:"Stage Velocity"},
  ];
  const scoreBarColor = (v) => v>=4?"#16A34A":v>=3?"#C2610C":"#DC2626";

  const trusted=[], caution=[], untrusted=[];
  if (areas) {
    if (areas.activityTracking >= 4) trusted.push("Deals have recent activity logged (last 30 days)");
    if (areas.fieldUsage >= 4) trusted.push("Field usage for amount + close date");
    if (areas.pipelineStages >= 4) trusted.push("Healthy stage distribution — not front-loaded");
    if (areas.dataIntegrity >= 4) trusted.push("No sandbagging or ghost wins detected");
    if (areas.dealCompleteness >= 4) trusted.push("Deals have contacts + next steps + deal type");
    if (areas.stageVelocity >= 4) trusted.push("Deals moving at healthy velocity");
    if (areas.pipelineQuality >= 4) trusted.push("Low single-threaded and zombie deal count");
    if (areas.pipelineStages <= 3) caution.push("Pipeline stage distribution skewed early");
    if (areas.activityTracking <= 3) caution.push("High % of deals with no activity in 30+ days");
    if (areas.forecastReliability <= 3) caution.push("Close date accuracy issues — stale or overdue dates");
    if (areas.stageVelocity <= 3) caution.push("Deals stuck longer than avg sales cycle");
    if (areas.dealCompleteness <= 3) caution.push("Open deals missing contacts, next step, or deal type");
    if (areas.pipelineQuality <= 3) caution.push("Single-threaded deals or zombie pipeline");
    if (areas.dataIntegrity <= 2) untrusted.push("Sandbagging or ghost wins detected");
    if (areas.dataCompleteness <= 2) untrusted.push("Critical fields missing on open deals");
    if (areas.forecastReliability <= 2) untrusted.push("Forecast unreliable — too many stale/overdue dates");
    if (areas.pipelineQuality <= 2) untrusted.push("Significant zombie pipeline — deals 12+ months old");
  }

  return (
    <div>
      <div style={{display:"grid",gridTemplateColumns:"200px 1fr",gap:14,marginBottom:14}}>
        {/* Score card */}
        <div style={{background:"#fff",border:"1px solid rgba(0,0,0,0.08)",borderRadius:12,padding:20,display:"flex",flexDirection:"column",alignItems:"center",justifyContent:"center",gap:6}}>
          <div style={{fontSize:10,fontWeight:700,color:"#555",textTransform:"uppercase",letterSpacing:"0.1em",marginBottom:4}}>CRM Score</div>
          <div style={{fontSize:64,fontWeight:800,color:scoreColor,lineHeight:1,fontFamily:"'DM Mono',monospace"}}>{score!==null?score:"—"}</div>
          <div style={{fontSize:13,color:"#555"}}>/10</div>
          <div style={{background:`${scoreColor}18`,border:`1px solid ${scoreColor}40`,borderRadius:8,padding:"7px 14px",fontSize:12,color:scoreColor,fontWeight:600,marginTop:6,textAlign:"center"}}>
            {score!==null?`${score>=8?"✅":score>=6?"⚠️":"🔴"} ${scoreLabel}`:"⚙️ Awaiting Live Data"}
          </div>
        </div>
        {/* Area scores */}
        <div style={{background:"#fff",border:"1px solid rgba(0,0,0,0.08)",borderRadius:12,padding:20}}>
          <SH title="Health by Area" sub="Score each area 1–5 based on live deal data"/>
          <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:10}}>
            {areaConfig.map(({key,label})=>{
              const v = areas?.[key]??null;
              const c = v!==null?scoreBarColor(v):"#555";
              return (
                <div key={key} style={{background:"#F8F8F8",border:"1px solid rgba(0,0,0,0.08)",borderRadius:9,padding:"12px 14px"}}>
                  <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:8}}>
                    <span style={{fontSize:12,fontWeight:600,color:"#111"}}>{label}</span>
                    <span style={{fontSize:13,fontWeight:800,color:c,fontFamily:"'DM Mono',monospace"}}>{v!==null?`${v}/5`:"—"}</span>
                  </div>
                  <div style={{background:"rgba(0,0,0,0.08)",borderRadius:3,height:5,overflow:"hidden"}}>
                    <div style={{width:v!==null?`${v/5*100}%`:"0%",height:"100%",background:c,borderRadius:3}}/>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {/* Trust tiers */}
      <div style={{display:"grid",gridTemplateColumns:"repeat(3,1fr)",gap:12,marginBottom:14}}>
        {[
          {title:"Can Be Trusted",    items:trusted.length?trusted:["Load live data to assess"],   color:"#16A34A",icon:"✅"},
          {title:"Use With Caution",  items:caution.length?caution:["Load live data to assess"],   color:"#C2610C",icon:"⚠️"},
          {title:"Cannot Be Trusted", items:untrusted.length?untrusted:metrics?["✓ Nothing flagged"]:["Load live data to assess"],color:untrusted.length?"#DC2626":"#16A34A",icon:untrusted.length?"🔴":"✅"},
        ].map((tier,i)=>(
          <div key={i} style={{background:"#fff",border:`1px solid ${tier.color}25`,borderRadius:12,padding:16}}>
            <div style={{fontSize:13,fontWeight:700,color:tier.color,marginBottom:12}}>{tier.icon} {tier.title}</div>
            {tier.items.map((item,j)=>(
              <div key={j} style={{display:"flex",alignItems:"flex-start",gap:8,marginBottom:8}}>
                <div style={{width:6,height:6,borderRadius:"50%",background:tier.color,marginTop:4,flexShrink:0}}/>
                <span style={{fontSize:12,color:"#999",lineHeight:1.5}}>{item}</span>
              </div>
            ))}
          </div>
        ))}
      </div>

      {/* Stats */}
      {metrics && (
        <Card>
          <SH title="Data Quality Summary" sub="From your live HubSpot deal data"/>
          <div style={{display:"grid",gridTemplateColumns:"repeat(4,1fr)",gap:10,marginBottom:10}}>
            <Tile label="Open Deals"       value={metrics.openDeals.length}    sub="total in pipeline"/>
            <Tile label="Stale Close Dates" value={metrics.stale}              sub={`${Math.round(metrics.staleRate*100)}% overdue`} color={metrics.staleRate>0.3?"#DC2626":metrics.staleRate>0.15?"#C2610C":"#16A34A"}/>
            <Tile label="Severely Overdue" value={metrics.severelyStale||0}    sub="30+ days past close" color={(metrics.severeStaleRate||0)>0.15?"#DC2626":(metrics.severeStaleRate||0)>0.05?"#C2610C":"#16A34A"}/>
            <Tile label="Zombie Deals"     value={(metrics.zombie||[]).length} sub="open 12+ months"    color={(metrics.zombieRate||0)>0.1?"#DC2626":(metrics.zombieRate||0)>0.05?"#C2610C":"#16A34A"}/>
          </div>
          <div style={{display:"grid",gridTemplateColumns:"repeat(4,1fr)",gap:10}}>
            <Tile label="Fields Complete"   value={completePct!=null?`${completePct}%`:"—"} sub="amount + date + owner" color={completePct>=85?"#16A34A":completePct>=60?"#C2610C":"#DC2626"}/>
            <Tile label="Dark Deals (30d)"  value={(metrics.darkDeals||[]).length} sub={`${Math.round((metrics.darkDealRate||0)*100)}% no activity`} color={(metrics.darkDealRate||0)>0.4?"#DC2626":(metrics.darkDealRate||0)>0.2?"#C2610C":"#16A34A"}/>
            <Tile label="Single-Threaded"   value={metrics.singleThreadRate!=null?`${Math.round(metrics.singleThreadRate*100)}%`:"—"} sub="deals with ≤1 contact" color={(metrics.singleThreadRate||0)>0.5?"#DC2626":(metrics.singleThreadRate||0)>0.3?"#C2610C":"#16A34A"}/>
            <Tile label="Missing Deal Type" value={metrics.dealTypePct!=null?`${Math.round((1-metrics.dealTypePct)*100)}%`:"—"} sub="no motion type" color={(1-(metrics.dealTypePct||1))>0.3?"#DC2626":(1-(metrics.dealTypePct||1))>0.1?"#C2610C":"#16A34A"}/>
          </div>
        </Card>
      )}

      {/* Integrity flags */}
      {metrics && (metrics.sandbagged?.length>0||metrics.ghostWins?.length>0||metrics.stuckDeals?.length>0||metrics.zombie?.length>0) && (
        <Card style={{marginTop:14}}>
          <SH title="Data Integrity Flags" sub="Deals that indicate sandbagging, ghost wins, or stagnation"/>
          <div style={{display:"grid",gridTemplateColumns:"repeat(3,1fr)",gap:12}}>
            {[
              {title:`🚩 Sandbagging (${metrics.sandbagged?.length||0})`, bg:"#FEF2F2", border:"#FECACA", tc:"#DC2626", bc:"#7F1D1D", desc:"Won deals closed within 3 days of creation.", deals:metrics.sandbagged||[]},
              {title:`👻 Ghost Wins (${metrics.ghostWins?.length||0})`,    bg:"#FFFBEB", border:"#FDE68A", tc:"#D97706", bc:"#92400E", desc:"Won deals with zero associated contacts.",  deals:metrics.ghostWins||[]},
              {title:`🧱 Stuck Deals (${metrics.stuckDeals?.length||0})`,  bg:"#F5F3FF", border:"#DDD6FE", tc:"#7C3AED", bc:"#4C1D95", desc:"Open deals aged 1.5× beyond avg sales cycle.", deals:metrics.stuckDeals||[]},
            ].map((p,i)=>(
              <div key={i} style={{background:p.bg,border:`1px solid ${p.border}`,borderRadius:10,padding:16}}>
                <div style={{fontSize:12,fontWeight:700,color:p.tc,marginBottom:4}}>{p.title}</div>
                <div style={{fontSize:11,color:p.bc,marginBottom:10,lineHeight:1.5}}>{p.desc}</div>
                {p.deals.slice(0,4).map((d,di)=>{
                  const F2=settings.fieldMap||{};
                  const name=d.properties?.[F2.dealname||"dealname"]||"Unnamed";
                  const amtV=+(d.properties?.[F2.amount||"amount"]||0);
                  const url=dealUrl(settings.hubspot?.portalId,d.id);
                  return (
                    <div key={di} onClick={()=>url&&window.open(url,"_blank")}
                      style={{padding:"5px 8px",background:"#fff",borderRadius:6,marginBottom:3,cursor:url?"pointer":"default",
                        display:"flex",justifyContent:"space-between",alignItems:"center",fontSize:11}}>
                      <span style={{color:"#111",fontWeight:600,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap",maxWidth:160}}>{name}</span>
                      <span style={{color:p.tc,fontWeight:700,fontFamily:"'DM Mono',monospace",flexShrink:0}}>{fmtM(amtV)}{url&&" ↗"}</span>
                    </div>
                  );
                })}
                {p.deals.length>4&&<div style={{fontSize:10,color:"#aaa",marginTop:3}}>+{p.deals.length-4} more</div>}
              </div>
            ))}
          </div>

          {/* Zombie deals */}
          {(metrics.zombie||[]).length>0&&(
            <div style={{background:"#F0FDF4",border:"1px solid #BBF7D0",borderRadius:10,padding:16,marginTop:12}}>
              <div style={{fontSize:12,fontWeight:700,color:"#15803D",marginBottom:4}}>🧟 Zombie Deals ({metrics.zombie.length})</div>
              <div style={{fontSize:11,color:"#14532D",marginBottom:10,lineHeight:1.5}}>Open deals created 12+ months ago. Inflate pipeline and distort coverage math.</div>
              <div style={{display:"flex",gap:6,flexWrap:"wrap"}}>
                {metrics.zombie.slice(0,8).map((d,i)=>{
                  const F2=settings.fieldMap||{};
                  const name=d.properties?.[F2.dealname||"dealname"]||"Unnamed";
                  const age=d.properties?.[F2.createdate||"createdate"]?Math.round((Date.now()-new Date(d.properties[F2.createdate||"createdate"]).getTime())/86400000):null;
                  const url=dealUrl(settings.hubspot?.portalId,d.id);
                  return (
                    <div key={i} onClick={()=>url&&window.open(url,"_blank")}
                      style={{padding:"5px 10px",background:"#fff",borderRadius:6,cursor:url?"pointer":"default",
                        border:"1px solid rgba(21,128,61,0.15)",fontSize:11,display:"flex",gap:8,alignItems:"center"}}>
                      <span style={{color:"#111",fontWeight:600}}>{name}</span>
                      <span style={{color:"#15803D",fontFamily:"'DM Mono',monospace",fontWeight:700}}>{age?`${age}d`:"old"}{url&&" ↗"}</span>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Won vs Lost field analysis */}
          {metrics.wonDealsFinal?.length>0&&metrics.lostDeals?.length>0&&(
            <div style={{marginTop:12}}>
              <div style={{fontSize:11,fontWeight:700,color:"#555",textTransform:"uppercase",letterSpacing:"0.07em",marginBottom:10}}>Won vs Lost — Field Correlation</div>
              <div style={{display:"grid",gridTemplateColumns:"1fr 120px 120px 80px",gap:8,marginBottom:8,padding:"6px 12px"}}>
                {["Field","Won %","Lost %","Gap"].map((h,i)=>(
                  <div key={i} style={{fontSize:10,fontWeight:700,color:"#555",textTransform:"uppercase",letterSpacing:"0.07em"}}>{h}</div>
                ))}
              </div>
              {[
                {key:"amount",label:"Has Amount"},
                {key:"closedate",label:"Has Close Date"},
                {key:"hubspot_owner_id",label:"Has Owner"},
                {key:"dealtype",label:"Has Deal Type"},
                {key:"num_associated_contacts",label:"Has Contacts",check:v=>+v>0},
              ].map(f=>{
                const wonWith =metrics.wonDealsFinal.filter(d=>f.check?f.check(d.properties[f.key]):!!d.properties[f.key]).length;
                const lostWith=metrics.lostDeals.filter(d=>f.check?f.check(d.properties[f.key]):!!d.properties[f.key]).length;
                const wonPct  =metrics.wonDealsFinal.length>0?Math.round(wonWith/metrics.wonDealsFinal.length*100):0;
                const lostPct =metrics.lostDeals.length>0?Math.round(lostWith/metrics.lostDeals.length*100):0;
                const gap=wonPct-lostPct;
                return (
                  <div key={f.key} style={{display:"grid",gridTemplateColumns:"1fr 120px 120px 80px",gap:8,alignItems:"center",padding:"8px 12px",marginBottom:4,background:"#fff",borderRadius:7}}>
                    <div style={{fontSize:12,color:"#111",fontWeight:500}}>{f.label}</div>
                    <div style={{display:"flex",alignItems:"center",gap:8}}>
                      <div style={{flex:1,background:"rgba(0,0,0,0.08)",borderRadius:3,height:5}}>
                        <div style={{width:`${wonPct}%`,height:"100%",background:"#16A34A",borderRadius:3}}/>
                      </div>
                      <span style={{fontSize:12,fontWeight:700,color:"#16A34A",minWidth:32}}>{wonPct}%</span>
                    </div>
                    <div style={{display:"flex",alignItems:"center",gap:8}}>
                      <div style={{flex:1,background:"rgba(0,0,0,0.08)",borderRadius:3,height:5}}>
                        <div style={{width:`${lostPct}%`,height:"100%",background:"#DC2626",borderRadius:3}}/>
                      </div>
                      <span style={{fontSize:12,fontWeight:700,color:"#DC2626",minWidth:32}}>{lostPct}%</span>
                    </div>
                    <span style={{fontSize:12,fontWeight:700,color:gap>20?"#16A34A":gap>0?"#D97706":"#555"}}>{gap>0?"+":""}{gap}pp</span>
                  </div>
                );
              })}
            </div>
          )}
        </Card>
      )}
    </div>
  );
};

// ═══════════════════════════════════════════════════════════════════════════════
// TAB 2 — Rep Hygiene Scorecards
// ═══════════════════════════════════════════════════════════════════════════════
const RepHygieneTab = ({ metrics, settings }) => {
  const [sortBy, setSortBy] = useState("score");
  const [sortDir, setSortDir] = useState("asc"); // asc = worst first
  const [expanded, setExpanded] = useState(null);

  const reps = useMemo(() => (settings?.team?.reps||[]).filter(r=>r.name), [settings]);
  const openDeals = metrics?.openDeals || [];
  const F = useMemo(() => resolveF(settings), [settings]);

  const repScores = useMemo(() => {
    if (!reps.length || !openDeals.length) return [];
    const now = Date.now();
    const cut14 = now - 14 * 86400000;
    const cut30 = now - 30 * 86400000;

    return reps.map(rep => {
      const oid = String(rep.hubspotOwnerId || "");
      // Match deals by owner ID if linked, otherwise show rep with no deals
      const deals = oid ? openDeals.filter(d =>
        String(d.properties?.[F.owner]||d.properties?.hubspot_owner_id||"") === oid
      ) : [];

      if (!deals.length) return { rep, oid, deals:[], score:null, dealCount:0,
        fieldsPct:0, nextStepPct:0, activityPct:0, closeDatePct:0, dealTypePct:0,
        issues:[{label:"Not linked to HubSpot owner", pct:0, color:"#aaa"}], darkDeals:[] };

      // 1. Fields complete — amount + close date
      const fieldsPct = deals.filter(d=>+d.properties?.[F.amount]>0 && d.properties?.[F.closedate]).length / deals.length * 100;

      // 2. Next step filled
      const nextStepPct = deals.filter(d=>!!d.properties?.[F.nextStep||"hs_next_step"]).length / deals.length * 100;

      // 3. Activity in last 14 days — ONLY notes_last_activity_date, not hs_lastmodifieddate
      const activityPct = deals.filter(d=>{
        const lastAct = d.properties?.notes_last_activity_date;
        return lastAct && new Date(lastAct).getTime() >= cut14;
      }).length / deals.length * 100;

      // 4. Close date accuracy — not overdue
      const closeDatePct = deals.filter(d=>{
        if (!d.properties?.[F.closedate]) return false;
        return new Date(d.properties[F.closedate]).getTime() >= now;
      }).length / deals.length * 100;

      // 5. Deal type filled
      const dealTypePct = deals.filter(d=>!!d.properties?.[F.dealtype||"dealtype"]).length / deals.length * 100;

      // Weighted hygiene score (0–100)
      const score = Math.round(
        fieldsPct * 0.25 +
        nextStepPct * 0.20 +
        activityPct * 0.30 +
        closeDatePct * 0.15 +
        dealTypePct * 0.10
      );

      // Top issues
      const issues = [];
      if (activityPct < 50)  issues.push({ label:"No activity 14d", pct: Math.round(activityPct), color:"#DC2626" });
      if (nextStepPct < 50)  issues.push({ label:"Missing next step", pct: Math.round(nextStepPct), color:"#D97706" });
      if (closeDatePct < 70) issues.push({ label:"Overdue close dates", pct: Math.round(closeDatePct), color:"#D97706" });
      if (fieldsPct < 80)    issues.push({ label:"Incomplete fields", pct: Math.round(fieldsPct), color:"#D97706" });
      if (dealTypePct < 60)  issues.push({ label:"Missing deal type", pct: Math.round(dealTypePct), color:"#555" });

      // Dark deals — no logged activity in 30 days (NOT hs_lastmodifieddate)
      const darkDeals = deals.filter(d=>{
        const lastAct = d.properties?.notes_last_activity_date;
        return !lastAct || new Date(lastAct).getTime() < cut30;
      });

      return { rep, oid, deals, dealCount:deals.length, score,
        fieldsPct: Math.round(fieldsPct), nextStepPct: Math.round(nextStepPct),
        activityPct: Math.round(activityPct), closeDatePct: Math.round(closeDatePct),
        dealTypePct: Math.round(dealTypePct), issues, darkDeals };
    }).sort((a,b) => {
      // Unlinked reps go to bottom, scored reps sorted worst first
      if (a.score === null && b.score !== null) return 1;
      if (a.score !== null && b.score === null) return -1;
      return (a.score||0) - (b.score||0);
    });
  }, [reps, openDeals, F]);

  const sorted = useMemo(() => {
    return [...repScores].sort((a,b) => {
      const av = a[sortBy] ?? 0, bv = b[sortBy] ?? 0;
      return sortDir === "asc" ? av - bv : bv - av;
    });
  }, [repScores, sortBy, sortDir]);

  const toggleSort = (col) => {
    if (sortBy === col) setSortDir(d => d==="asc"?"desc":"asc");
    else { setSortBy(col); setSortDir("asc"); }
  };

  const scoredReps = repScores.filter(r => r.dealCount > 0);
  const teamAvg = scoredReps.length ? Math.round(scoredReps.reduce((a,r)=>a+(r.score||0),0)/scoredReps.length) : null;

  const Th = ({col, label}) => (
    <div onClick={()=>toggleSort(col)} style={{fontSize:9,fontWeight:700,color:"#aaa",textTransform:"uppercase",letterSpacing:"0.06em",cursor:"pointer",userSelect:"none",display:"flex",alignItems:"center",gap:3}}>
      {label}{sortBy===col&&(sortDir==="asc"?<ChevronUp size={9}/>:<ChevronDown size={9}/>)}
    </div>
  );

  if (!reps.length) return (
    <div style={{textAlign:"center",padding:40,color:"#aaa"}}>
      <div style={{fontSize:32,marginBottom:12}}>👥</div>
      <div style={{fontSize:13,fontWeight:600,color:"#555"}}>No reps configured</div>
      <div style={{fontSize:11,marginTop:6}}>Add reps in Setup → Quota & Team to see hygiene scores</div>
    </div>
  );

  if (!openDeals.length) return (
    <div style={{textAlign:"center",padding:40,color:"#aaa"}}>
      <div style={{fontSize:32,marginBottom:12}}>📊</div>
      <div style={{fontSize:13,fontWeight:600,color:"#555"}}>Load deal data first</div>
      <div style={{fontSize:11,marginTop:6}}>Click "Load Deals" in the sidebar to pull live data</div>
    </div>
  );

  return (
    <div>
      {/* Team summary */}
      <div style={{display:"grid",gridTemplateColumns:"repeat(5,1fr)",gap:12,marginBottom:20}}>
        {[
          {label:"Team Avg Score",    value:teamAvg!=null?`${teamAvg}/100`:"—", color:teamAvg!=null?clrS(teamAvg):"#555"},
          {label:"Reps w/ Deals",     value:`${scoredReps.length}/${repScores.length}`, color:"#111"},
          {label:"Avg Activity Rate", value:scoredReps.length?`${Math.round(scoredReps.reduce((a,r)=>a+r.activityPct,0)/scoredReps.length)}%`:"—", color:"#111"},
          {label:"Avg Field Complete",value:scoredReps.length?`${Math.round(scoredReps.reduce((a,r)=>a+r.fieldsPct,0)/scoredReps.length)}%`:"—", color:"#111"},
          {label:"Avg Next Step",     value:scoredReps.length?`${Math.round(scoredReps.reduce((a,r)=>a+r.nextStepPct,0)/scoredReps.length)}%`:"—", color:"#111"},
        ].map((t,i)=><Tile key={i} {...t}/>)}
      </div>

      {/* Scorecard table */}
      <Card>
        <SH title="Rep Hygiene Scorecards" sub="Sorted by worst score first — click any row to expand. Score = weighted avg of 5 hygiene signals."/>

        {/* Header */}
        <div style={{display:"grid",gridTemplateColumns:"1fr 80px 90px 90px 90px 90px 90px 28px",gap:8,padding:"8px 12px",background:"#F8F8F8",borderRadius:7,marginBottom:4}}>
          <Th col="rep.name" label="Rep"/>
          <Th col="score"    label="Score"/>
          <Th col="activityPct"  label="Activity"/>
          <Th col="fieldsPct"    label="Fields"/>
          <Th col="nextStepPct"  label="Next Step"/>
          <Th col="closeDatePct" label="Close Dates"/>
          <Th col="dealTypePct"  label="Deal Type"/>
          <div/>
        </div>

        {sorted.map((r,i) => {
          const isExp = expanded === r.oid;
          const isUnlinked = !r.oid || r.dealCount === 0;
          const scoreColor = isUnlinked ? "#aaa" : clrS(r.score||0);
          const scoreBg    = isUnlinked ? "rgba(0,0,0,0.04)" : bgS(r.score||0);
          return (
            <React.Fragment key={r.oid}>
              <div onClick={() => setExpanded(isExp?null:r.oid)}
                style={{display:"grid",gridTemplateColumns:"1fr 80px 90px 90px 90px 90px 90px 28px",gap:8,
                  padding:"10px 12px",borderBottom:"1px solid rgba(0,0,0,0.04)",
                  background:isExp?"rgba(204,26,26,0.02)":i%2?"rgba(0,0,0,0.01)":"#fff",
                  cursor:"pointer",alignItems:"center"}}>
                <div>
                  <div style={{fontSize:12,fontWeight:700,color:"#111"}}>{r.rep.name}</div>
                  {r.rep.team&&<div style={{fontSize:9,color:"#aaa"}}>{r.rep.team}</div>}
                </div>
                {/* Score badge */}
                <div style={{background:scoreBg,borderRadius:6,padding:"4px 8px",textAlign:"center"}}>
                  {isUnlinked ? (
                    <div style={{fontSize:10,color:"#aaa",fontWeight:600,whiteSpace:"nowrap"}}>No HubSpot<br/>owner linked</div>
                  ) : (
                    <>
                      <div style={{fontSize:14,fontWeight:900,color:scoreColor,fontFamily:"'DM Mono',monospace"}}>{r.score}</div>
                      <div style={{fontSize:8,color:scoreColor,fontWeight:700}}>{r.score>=80?"Good":r.score>=60?"Fair":"Poor"}</div>
                    </>
                  )}
                </div>
                {/* Metric bars */}
                {isUnlinked ? (
                  [0,1,2,3,4].map(ci=>(
                    <div key={ci} style={{display:"flex",alignItems:"center",justifyContent:"center"}}>
                      <span style={{fontSize:11,color:"#ddd"}}>—</span>
                    </div>
                  ))
                ) : (
                  [r.activityPct, r.fieldsPct, r.nextStepPct, r.closeDatePct, r.dealTypePct].map((v,ci)=>(
                    <div key={ci}>
                      <div style={{display:"flex",justifyContent:"space-between",marginBottom:2}}>
                        <span style={{fontSize:10,fontWeight:700,color:clrS(v)}}>{v}%</span>
                        <span style={{fontSize:9,color:"#aaa"}}>{r.dealCount}d</span>
                      </div>
                      <div style={{height:4,background:"rgba(0,0,0,0.08)",borderRadius:2,overflow:"hidden"}}>
                        <div style={{width:`${v}%`,height:"100%",background:clrS(v),borderRadius:2}}/>
                      </div>
                    </div>
                  ))
                )}
                <div style={{color:isExp?"#CC1A1A":"#aaa",fontSize:12,textAlign:"center"}}>
                  {isExp?<ChevronUp size={13}/>:<ChevronDown size={13}/>}
                </div>
              </div>

              {/* Expanded detail */}
              {isExp && (
                <div style={{padding:"12px 16px",background:"rgba(204,26,26,0.015)",borderBottom:"2px solid rgba(204,26,26,0.08)"}}>
                  <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:16}}>
                    {/* Issues */}
                    <div>
                      <div style={{fontSize:11,fontWeight:700,color:"#555",textTransform:"uppercase",letterSpacing:"0.07em",marginBottom:8}}>Top Issues</div>
                      {r.issues.length===0
                        ? <div style={{fontSize:12,color:"#16A34A",fontWeight:600}}>✓ No major hygiene issues</div>
                        : r.issues.map((issue,ii)=>(
                          <div key={ii} style={{display:"flex",justifyContent:"space-between",alignItems:"center",padding:"6px 10px",background:"#fff",borderRadius:7,marginBottom:4,border:`1px solid ${issue.color}20`}}>
                            <span style={{fontSize:12,color:"#333"}}>{issue.label}</span>
                            <span style={{fontSize:11,fontWeight:700,color:issue.color}}>{issue.pct}% complete</span>
                          </div>
                        ))
                      }
                    </div>
                    {/* Dark deals */}
                    <div>
                      <div style={{fontSize:11,fontWeight:700,color:"#555",textTransform:"uppercase",letterSpacing:"0.07em",marginBottom:8}}>
                        Dark Deals — No Activity 30d ({r.darkDeals.length})
                      </div>
                      {r.darkDeals.length===0
                        ? <div style={{fontSize:12,color:"#16A34A",fontWeight:600}}>✓ All deals have recent activity</div>
                        : r.darkDeals.slice(0,5).map((d,di)=>{
                            const name = d.properties?.[F.dealname||"dealname"]||"Unnamed";
                            const amtV = +(d.properties?.[F.amount||"amount"]||0);
                            const url  = dealUrl(settings.hubspot?.portalId, d.id);
                            return (
                              <div key={di} onClick={e=>{e.stopPropagation();url&&window.open(url,"_blank")}}
                                style={{display:"flex",justifyContent:"space-between",padding:"5px 10px",background:"#fff",borderRadius:6,marginBottom:3,cursor:url?"pointer":"default",border:"1px solid rgba(220,38,38,0.08)"}}>
                                <span style={{fontSize:11,color:"#111",fontWeight:600,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap",maxWidth:180}}>{name}</span>
                                <span style={{fontSize:11,color:"#DC2626",fontFamily:"'DM Mono',monospace",fontWeight:700,flexShrink:0}}>{fmtM(amtV)}{url&&" ↗"}</span>
                              </div>
                            );
                          })
                      }
                    </div>
                  </div>
                </div>
              )}
            </React.Fragment>
          );
        })}
      </Card>

      {/* Offender tables */}
      <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:14,marginTop:14}}>
        {[
          { title:"Worst Activity Loggers",   key:"activityPct",  desc:"Reps with fewest deals touched in last 14 days", color:"#DC2626" },
          { title:"Most Overdue Close Dates",  key:"closeDatePct", desc:"Reps with most past-due deals",                  color:"#D97706" },
          { title:"Missing Next Steps",        key:"nextStepPct",  desc:"Reps leaving deals with no documented next action", color:"#7C3AED" },
          { title:"Incomplete Fields",         key:"fieldsPct",    desc:"Reps with deals missing amount or close date",   color:"#0891B2" },
        ].map((t,ti)=>(
          <Card key={ti}>
            <div style={{fontSize:12,fontWeight:700,color:t.color,marginBottom:4}}>{t.title}</div>
            <div style={{fontSize:11,color:"#888",marginBottom:12}}>{t.desc}</div>
            {[...repScores].sort((a,b)=>a[t.key]-b[t.key]).slice(0,5).map((r,ri)=>(
              <div key={ri} style={{display:"flex",alignItems:"center",gap:10,marginBottom:8}}>
                <div style={{width:6,height:6,borderRadius:"50%",background:t.color,flexShrink:0}}/>
                <span style={{fontSize:12,fontWeight:600,color:"#111",flex:1}}>{r.rep.name}</span>
                <div style={{flex:2}}>
                  <div style={{height:5,background:"rgba(0,0,0,0.08)",borderRadius:3,overflow:"hidden"}}>
                    <div style={{width:`${r[t.key]}%`,height:"100%",background:clrS(r[t.key]),borderRadius:3}}/>
                  </div>
                </div>
                <span style={{fontSize:11,fontWeight:700,color:clrS(r[t.key]),minWidth:36,textAlign:"right"}}>{r[t.key]}%</span>
              </div>
            ))}
          </Card>
        ))}
      </div>
    </div>
  );
};

// ═══════════════════════════════════════════════════════════════════════════════
// TAB 3 — Stage Analysis
// ═══════════════════════════════════════════════════════════════════════════════
const StageAnalysisTab = ({ metrics, settings }) => {
  const openDeals = metrics?.openDeals || [];
  const mappings  = settings?.stages?.mappings || [];
  const F = useMemo(() => resolveF(settings), [settings]);

  const stageData = useMemo(() => {
    if (!openDeals.length || !mappings.length) return [];
    const now = Date.now();
    const pipelineStages = mappings.filter(m=>m.type==="pipeline");

    return pipelineStages.map(stage => {
      const deals = openDeals.filter(d =>
        (d.properties?.[F.dealstage||"dealstage"]||"").toLowerCase() === stage.id.toLowerCase()
      );
      const totalVal = deals.reduce((a,d)=>a+(+(d.properties?.[F.amount||"amount"]||0)),0);
      const ages = deals.map(d=>{
        if (!d.properties?.[F.createdate||"createdate"]) return 0;
        return Math.round((now - new Date(d.properties[F.createdate||"createdate"]).getTime()) / 86400000);
      }).filter(a=>a>0);
      const avgAge = ages.length ? Math.round(ages.reduce((a,v)=>a+v,0)/ages.length) : 0;
      const stalled = deals.filter(d=>{
        const lastAct = d.properties?.notes_last_activity_date || d.properties?.hs_lastmodifieddate;
        if (!lastAct) return true;
        return (now - new Date(lastAct).getTime()) > 21 * 86400000;
      });

      return { stage: stage.label||stage.id, count: deals.length, val: totalVal, avgAge, stalled: stalled.length };
    }).filter(s=>s.count>0).sort((a,b)=>b.val-a.val);
  }, [openDeals, mappings, F]);

  const maxVal = Math.max(...stageData.map(s=>s.val), 1);

  if (!openDeals.length) return <div style={{textAlign:"center",padding:40,color:"#aaa",fontSize:13}}>Load deal data to see stage analysis</div>;

  return (
    <div>
      <SH title="Pipeline by Stage" sub="Deal count, value, average age, and stalled deals per stage"/>
      <Card>
        {/* Header */}
        <div style={{display:"grid",gridTemplateColumns:"160px 1fr 80px 80px 80px 80px",gap:12,padding:"8px 12px",background:"#F8F8F8",borderRadius:7,marginBottom:4}}>
          {["Stage","Pipeline Value","Deals","Avg Age","Stalled","% Total"].map((h,i)=>(
            <div key={i} style={{fontSize:9,fontWeight:700,color:"#aaa",textTransform:"uppercase",letterSpacing:"0.06em"}}>{h}</div>
          ))}
        </div>
        {stageData.map((s,i)=>{
          const pctOfTotal = maxVal>0?s.val/maxVal*100:0;
          const stallPct = s.count>0?Math.round(s.stalled/s.count*100):0;
          return (
            <div key={i} style={{display:"grid",gridTemplateColumns:"160px 1fr 80px 80px 80px 80px",gap:12,padding:"10px 12px",borderBottom:i<stageData.length-1?"1px solid rgba(0,0,0,0.04)":"none",alignItems:"center"}}>
              <div style={{fontSize:12,fontWeight:600,color:"#111"}}>{s.stage}</div>
              <div>
                <div style={{display:"flex",justifyContent:"space-between",marginBottom:3}}>
                  <span style={{fontSize:12,fontWeight:700,color:"#7C3AED",fontFamily:"'DM Mono',monospace"}}>{fmtM(s.val)}</span>
                </div>
                <div style={{height:5,background:"rgba(0,0,0,0.06)",borderRadius:3,overflow:"hidden"}}>
                  <div style={{width:`${pctOfTotal}%`,height:"100%",background:"#7C3AED",borderRadius:3}}/>
                </div>
              </div>
              <div style={{fontSize:12,color:"#555",textAlign:"center"}}>{s.count}</div>
              <div style={{fontSize:12,color:s.avgAge>60?"#DC2626":s.avgAge>30?"#D97706":"#555",textAlign:"center",fontFamily:"'DM Mono',monospace"}}>{s.avgAge}d</div>
              <div style={{textAlign:"center"}}>
                <div style={{fontSize:12,fontWeight:700,color:stallPct>50?"#DC2626":stallPct>25?"#D97706":"#16A34A"}}>{s.stalled}</div>
                <div style={{fontSize:9,color:"#aaa"}}>{stallPct}% stalled</div>
              </div>
              <div style={{fontSize:11,color:"#555",textAlign:"center"}}>{maxVal>0?`${Math.round(s.val/stageData.reduce((a,st)=>a+st.val,0)*100)}%`:"—"}</div>
            </div>
          );
        })}
      </Card>

      {/* Stall heatmap */}
      {stageData.some(s=>s.stalled>0) && (
        <Card style={{marginTop:14}}>
          <SH title="Stall Heatmap" sub="Deals with no activity in 21+ days by stage — where does pipeline go to die?"/>
          <div style={{display:"flex",gap:8,flexWrap:"wrap"}}>
            {stageData.filter(s=>s.stalled>0).sort((a,b)=>b.stalled-a.stalled).map((s,i)=>{
              const intensity = Math.min(1, s.stalled / Math.max(...stageData.map(x=>x.stalled)));
              const r = Math.round(220 + (intensity * 35));
              const g = Math.round(38 - (intensity * 20));
              const b2 = Math.round(38 - (intensity * 20));
              return (
                <div key={i} style={{padding:"12px 16px",borderRadius:10,background:`rgba(${r},${g},${b2},${0.08+intensity*0.12})`,
                  border:`1px solid rgba(${r},${g},${b2},${0.2+intensity*0.2})`,minWidth:120,textAlign:"center"}}>
                  <div style={{fontSize:24,fontWeight:900,color:`rgb(${r},${g},${b2})`,fontFamily:"'DM Mono',monospace"}}>{s.stalled}</div>
                  <div style={{fontSize:11,fontWeight:700,color:`rgb(${r},${g},${b2})`}}>{s.stage}</div>
                  <div style={{fontSize:10,color:"#888",marginTop:2}}>{Math.round(s.stalled/s.count*100)}% of stage</div>
                </div>
              );
            })}
          </div>
        </Card>
      )}
    </div>
  );
};

// ═══════════════════════════════════════════════════════════════════════════════
// TAB 4 — Company Health
// ═══════════════════════════════════════════════════════════════════════════════
const LIFECYCLE_CONFIG = {
  lead:                   { label:"Lead",           color:"#7C3AED", order:1 },
  marketingqualifiedlead: { label:"MQL",            color:"#CC1A1A", order:2 },
  salesqualifiedlead:     { label:"SQL",            color:"#DC2626", order:3 },
  opportunity:            { label:"Opportunity",    color:"#D97706", order:4 },
  customer:               { label:"Customer",       color:"#16A34A", order:5 },
  evangelist:             { label:"Evangelist",     color:"#0891B2", order:6 },
  subscriber:             { label:"Subscriber",     color:"#555",    order:7 },
  other:                  { label:"Other",          color:"#888",    order:8 },
};

const CompanyHealthTab = ({ metrics, settings, companies=[] }) => {

  if (!companies.length) return (
    <div style={{textAlign:"center",padding:60,color:"#aaa"}}>
      <div style={{fontSize:32,marginBottom:12}}>🏢</div>
      <div style={{fontSize:13,fontWeight:600,color:"#555",marginBottom:6}}>No company data loaded</div>
      <div style={{fontSize:11}}>Click "Load Accounts" in the sidebar to pull company data</div>
    </div>
  );

  const total = companies.length;
  const now   = Date.now();
  const cut30 = now - 30 * 86400000;
  const cut90 = now - 90 * 86400000;

  // Ownership
  const withOwner    = companies.filter(c=>!!c.properties?.hubspot_owner_id).length;
  const withoutOwner = total - withOwner;
  const ownerPct     = Math.round(withOwner/total*100);

  // Activity
  const withActivity30 = companies.filter(c=>{
    const a = c.properties?.notes_last_activity_date;
    return a && new Date(a).getTime() >= cut30;
  }).length;
  const withActivity90 = companies.filter(c=>{
    const a = c.properties?.notes_last_activity_date;
    return a && new Date(a).getTime() >= cut90;
  }).length;
  const neverTouched = companies.filter(c=>!c.properties?.notes_last_activity_date).length;

  // Lifecycle stage breakdown
  const byStage = {};
  companies.forEach(c=>{
    const s = c.properties?.lifecyclestage || "unknown";
    if (!byStage[s]) byStage[s] = 0;
    byStage[s]++;
  });
  const stageRows = Object.entries(byStage)
    .map(([s,count])=>({ s, count, cfg: LIFECYCLE_CONFIG[s]||{label:s,color:"#aaa",order:99} }))
    .sort((a,b)=>a.cfg.order-b.cfg.order);

  // Unowned by lifecycle — the critical ones
  const unownedByStage = {};
  companies.filter(c=>!c.properties?.hubspot_owner_id).forEach(c=>{
    const s = c.properties?.lifecyclestage || "unknown";
    if (!unownedByStage[s]) unownedByStage[s] = 0;
    unownedByStage[s]++;
  });
  const highPriorityUnowned = ["marketingqualifiedlead","salesqualifiedlead","opportunity","customer"]
    .map(s=>({ s, count:unownedByStage[s]||0, cfg:LIFECYCLE_CONFIG[s] }))
    .filter(r=>r.count>0);

  // Field completion
  const fieldStats = [
    { label:"Has Owner",         count:withOwner,                                                                             color:"#CC1A1A" },
    { label:"Has Lifecycle Stage",count:companies.filter(c=>!!c.properties?.lifecyclestage).length,                          color:"#7C3AED" },
    { label:"Has Industry",       count:companies.filter(c=>!!c.properties?.industry).length,                                color:"#D97706" },
    { label:"Has Employee Count", count:companies.filter(c=>+c.properties?.numberofemployees>0).length,                     color:"#0891B2" },
    { label:"Has Annual Revenue", count:companies.filter(c=>+c.properties?.annualrevenue>0).length,                         color:"#16A34A" },
    { label:"Has Phone",          count:companies.filter(c=>!!c.properties?.phone).length,                                   color:"#555"    },
    { label:"Activity (30d)",     count:withActivity30,                                                                       color:"#16A34A" },
    { label:"Activity (90d)",     count:withActivity90,                                                                       color:"#D97706" },
  ].map(f=>({...f, pct:Math.round(f.count/total*100)}));

  return (
    <div>
      {/* Summary tiles */}
      <div style={{display:"grid",gridTemplateColumns:"repeat(4,1fr)",gap:12,marginBottom:20}}>
        {[
          { label:"Total Loaded",     v:total.toLocaleString(),                         s:"companies",               c:"#111" },
          { label:"Owner Assigned",   v:`${ownerPct}%`,                                  s:`${withOwner.toLocaleString()} of ${total.toLocaleString()}`, c:ownerPct>=80?"#16A34A":ownerPct>=50?"#D97706":"#DC2626" },
          { label:"No Owner",         v:withoutOwner.toLocaleString(),                   s:"unassigned accounts",     c:withoutOwner>0?"#DC2626":"#16A34A" },
          { label:"Never Touched",    v:neverTouched.toLocaleString(),                   s:"no activity ever logged", c:Math.round(neverTouched/total*100)>50?"#DC2626":"#D97706" },
        ].map((t,i)=><Tile key={i} label={t.label} value={t.v} sub={t.s} color={t.c}/>)}
      </div>

      {/* High-priority unowned alert */}
      {highPriorityUnowned.length > 0 && (
        <div style={{padding:"14px 18px",background:"rgba(220,38,38,0.05)",border:"1px solid rgba(220,38,38,0.15)",borderRadius:12,marginBottom:20}}>
          <div style={{fontSize:13,fontWeight:800,color:"#DC2626",marginBottom:8}}>🚨 Unowned High-Value Accounts</div>
          <div style={{display:"flex",gap:12,flexWrap:"wrap"}}>
            {highPriorityUnowned.map((r,i)=>(
              <div key={i} style={{padding:"8px 14px",background:"#fff",borderRadius:8,border:`1px solid ${r.cfg.color}30`}}>
                <div style={{fontSize:10,fontWeight:700,color:r.cfg.color,textTransform:"uppercase",letterSpacing:"0.06em"}}>{r.cfg.label}</div>
                <div style={{fontSize:20,fontWeight:900,color:r.cfg.color,fontFamily:"'DM Mono',monospace"}}>{r.count.toLocaleString()}</div>
                <div style={{fontSize:10,color:"#888"}}>no owner assigned</div>
              </div>
            ))}
          </div>
          <div style={{fontSize:11,color:"#888",marginTop:10,lineHeight:1.6}}>
            These accounts are in active buying stages but have no rep assigned. Go to Account Coverage and check the Unassigned Territory panel to see which companies need routing.
          </div>
        </div>
      )}

      <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:16}}>
        {/* Field completion */}
        <Card>
          <SH title="Field Completion" sub="How well populated are key company fields"/>
          {fieldStats.map((f,i)=>(
            <div key={i} style={{marginBottom:12}}>
              <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:4}}>
                <span style={{fontSize:12,fontWeight:600,color:"#333"}}>{f.label}</span>
                <div style={{display:"flex",alignItems:"center",gap:8}}>
                  <span style={{fontSize:11,color:"#888"}}>{f.count.toLocaleString()}</span>
                  <span style={{fontSize:12,fontWeight:800,color:f.pct>=80?"#16A34A":f.pct>=50?"#D97706":"#DC2626",
                    fontFamily:"'DM Mono',monospace",minWidth:36,textAlign:"right"}}>{f.pct}%</span>
                </div>
              </div>
              <div style={{height:5,background:"rgba(0,0,0,0.07)",borderRadius:3,overflow:"hidden"}}>
                <div style={{width:`${f.pct}%`,height:"100%",borderRadius:3,
                  background:f.pct>=80?"#16A34A":f.pct>=50?"#D97706":"#DC2626"}}/>
              </div>
            </div>
          ))}
        </Card>

        {/* Lifecycle stage breakdown */}
        <Card>
          <SH title="Lifecycle Stage Breakdown" sub="How accounts are distributed across the funnel"/>
          {stageRows.map((r,i)=>{
            const unowned = unownedByStage[r.s]||0;
            const unownedPct = r.count>0?Math.round(unowned/r.count*100):0;
            return (
              <div key={i} style={{marginBottom:12}}>
                <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:4}}>
                  <div style={{display:"flex",alignItems:"center",gap:6}}>
                    <div style={{width:8,height:8,borderRadius:"50%",background:r.cfg.color,flexShrink:0}}/>
                    <span style={{fontSize:12,fontWeight:600,color:"#333"}}>{r.cfg.label}</span>
                  </div>
                  <div style={{display:"flex",alignItems:"center",gap:8}}>
                    <span style={{fontSize:10,color:"#aaa"}}>{r.count.toLocaleString()}</span>
                    {unowned>0&&<span style={{fontSize:9,fontWeight:700,color:"#DC2626",background:"rgba(220,38,38,0.08)",
                      borderRadius:4,padding:"1px 5px"}}>{unownedPct}% unowned</span>}
                    <span style={{fontSize:12,fontWeight:800,color:r.cfg.color,
                      fontFamily:"'DM Mono',monospace",minWidth:36,textAlign:"right"}}>
                      {Math.round(r.count/total*100)}%
                    </span>
                  </div>
                </div>
                <div style={{height:5,background:"rgba(0,0,0,0.07)",borderRadius:3,overflow:"hidden"}}>
                  <div style={{width:`${r.count/total*100}%`,height:"100%",background:r.cfg.color,borderRadius:3}}/>
                </div>
              </div>
            );
          })}
        </Card>
      </div>
    </div>
  );
};

// ═══════════════════════════════════════════════════════════════════════════════
// Main CRMHealth
// ═══════════════════════════════════════════════════════════════════════════════
const CRMHealth = ({settings, onGo, metrics, companies=[]}) => {
  const [tab, setTab] = useState("overview");
  if (!settings.stages?.configured) return <div className="fade-up"><SetupBanner section="HubSpot Stage Mapping" onGo={onGo}/></div>;

  const openDeals = metrics?.openDeals || [];
  const reps = (settings?.team?.reps||[]).filter(r=>r.name);

  const TABS = [
    { id:"overview", label:"Overview" },
    { id:"hygiene",  label:"Rep Hygiene", badge: reps.length },
    { id:"stages",   label:"Stage Analysis" },
    { id:"company",  label:"Company Health" },
  ];

  return (
    <div className="fade-up">
      {/* Tab bar */}
      <div style={{display:"flex",gap:6,marginBottom:20,background:"#F8F8F8",borderRadius:10,padding:4,width:"fit-content"}}>
        {TABS.map(t=><Tab key={t.id} {...t} active={tab===t.id} onClick={setTab}/>)}
      </div>

      {tab==="overview" && <OverviewTab metrics={metrics} settings={settings}/>}
      {tab==="hygiene"  && <RepHygieneTab metrics={metrics} settings={settings}/>}
      {tab==="stages"   && <StageAnalysisTab metrics={metrics} settings={settings}/>}
      {tab==="company"  && <CompanyHealthTab metrics={metrics} settings={settings} companies={companies}/>}
    </div>
  );
};

export default CRMHealth;
